import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Check, Tag, X, Download, Quote } from "lucide-react";
import { toast } from "sonner";
import SyllabusModal from "../components/SyllabusModal";

export default function ProgramDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [program, setProgram] = useState(null);
  const [error, setError] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [refCode, setRefCode] = useState("");
  const [refApplied, setRefApplied] = useState(null);
  const [useCredit, setUseCredit] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState("full"); // "full" | "instalments_3"
  const [showSyllabus, setShowSyllabus] = useState(false);

  useEffect(() => {
    api.get(`/programs/${slug}`)
      .then((r) => setProgram(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Program not found"));
  }, [slug]);

  const applyReferral = async () => {
    if (!refCode.trim()) return;
    if (!user) {
      toast.info("Sign in to apply a referral code");
      return;
    }
    try {
      const { data } = await api.post("/referrals/validate", { code: refCode.trim() });
      setRefApplied(data);
      toast.success(`Referral applied · ${formatINR(data.discount_inr)} off`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invalid code");
      setRefApplied(null);
    }
  };

  const clearReferral = () => { setRefCode(""); setRefApplied(null); };

  const base = program?.price_inr || 0;
  const credit = user?.referral_credit_inr || 0;
  const discount = refApplied?.discount_inr || 0;
  const creditApplied = useCredit ? Math.min(credit, Math.max(0, base - discount)) : 0;
  const totalAfterDiscount = Math.max(1, base - discount - creditApplied);
  const isInstalments = paymentPlan === "instalments_3";
  const instalmentAmount = Math.round((totalAfterDiscount / 3) * 100) / 100;
  const finalAmount = isInstalments ? instalmentAmount : totalAfterDiscount;

  const handleEnroll = async () => {
    if (!user) {
      nav("/login?next=" + encodeURIComponent(`/programs/${slug}`));
      return;
    }
    setEnrolling(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        program_slug: slug,
        origin_url: window.location.origin,
        referral_code: refApplied ? refCode.trim() : null,
        use_referral_credit: useCredit,
        payment_plan: paymentPlan,
      });
      // Auto-submit form to PayU hosted checkout
      const formEl = document.createElement("form");
      formEl.method = "POST";
      formEl.action = data.action;
      Object.entries(data.fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = String(v);
        formEl.appendChild(input);
      });
      document.body.appendChild(formEl);
      formEl.submit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setEnrolling(false);
    }
  };

  if (error) return <div className="max-w-[1400px] mx-auto px-6 py-20 text-novya-muted">{error}</div>;
  if (!program) return <div data-testid="program-loading" className="max-w-[1400px] mx-auto px-6 py-20 font-mono text-xs tracking-[0.3em] uppercase text-novya-muted">Loading…</div>;

  return (
    <div data-testid="program-detail-page">
      {/* Hero */}
      <section className="border-b border-novya-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted">
              Track · {program.format}
            </div>
            <h1 className="mt-6 font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.95] text-novya-ink">
              {program.title}
            </h1>
            <p className="mt-6 text-base md:text-lg text-novya-muted leading-relaxed max-w-xl">
              {program.tagline}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-6">
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted">Duration</div>
                <div className="mt-1 font-display font-bold text-2xl text-novya-ink">{program.duration}</div>
              </div>
              <div className="w-px h-10 bg-novya-border" />
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted">Tuition</div>
                <div className="mt-1 font-display font-bold text-2xl text-novya-ink" data-testid="program-price">
                  {formatINR(program.price_inr)}
                </div>
              </div>
              <div className="w-px h-10 bg-novya-border" />
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted">Format</div>
                <div className="mt-1 font-display font-bold text-2xl text-novya-ink">{program.format}</div>
              </div>
            </div>

            {/* Referral + credit + enroll block */}
            <div className="mt-10 border border-novya-border p-6 md:p-8 bg-white max-w-xl">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted mb-4">Discounts</div>
              <div className="flex items-stretch gap-2">
                <div className="flex-1 flex items-center gap-2 border border-novya-border px-3">
                  <Tag size={14} className="text-novya-muted shrink-0" />
                  <input
                    data-testid="referral-code-input"
                    value={refCode}
                    onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                    placeholder="Referral code (optional)"
                    className="w-full py-3 bg-transparent outline-none text-sm font-mono tracking-wide"
                    disabled={!!refApplied}
                  />
                  {refApplied && (
                    <button onClick={clearReferral} data-testid="referral-clear-btn" className="text-novya-muted hover:text-novya-ink">
                      <X size={14} />
                    </button>
                  )}
                </div>
                <button
                  data-testid="referral-apply-btn"
                  onClick={applyReferral}
                  disabled={!!refApplied || !refCode.trim()}
                  className="px-5 border border-novya-ink text-novya-ink font-medium text-sm hover:bg-novya-ink hover:text-white transition-colors disabled:opacity-40"
                >
                  {refApplied ? "Applied" : "Apply"}
                </button>
              </div>

              {user && credit > 0 && (
                <label className="mt-4 flex items-center gap-3 cursor-pointer" data-testid="use-credit-label">
                  <input
                    type="checkbox"
                    checked={useCredit}
                    onChange={(e) => setUseCredit(e.target.checked)}
                    data-testid="use-credit-checkbox"
                    className="w-4 h-4 accent-[#0047FF]"
                  />
                  <span className="text-sm text-novya-ink">
                    Use my referral credit — <span className="font-mono">{formatINR(credit)} available</span>
                  </span>
                </label>
              )}

              <div className="mt-6 border-t border-novya-border pt-4">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted mb-3">Payment plan</div>
                <div className="grid grid-cols-2 gap-2" data-testid="payment-plan-toggle">
                  <button
                    type="button"
                    onClick={() => setPaymentPlan("full")}
                    data-testid="plan-full-btn"
                    className={`p-3 border text-left transition-colors ${paymentPlan === "full" ? "border-novya-ink bg-novya-ink text-white" : "border-novya-border text-novya-ink hover:border-novya-ink"}`}
                  >
                    <div className="font-medium text-sm">Pay in full</div>
                    <div className="mt-1 font-mono text-[11px] opacity-70">{formatINR(totalAfterDiscount)} today</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentPlan("instalments_3")}
                    data-testid="plan-instalments-btn"
                    className={`p-3 border text-left transition-colors ${isInstalments ? "border-novya-accent bg-novya-accent text-white" : "border-novya-border text-novya-ink hover:border-novya-ink"}`}
                  >
                    <div className="font-medium text-sm">3 monthly instalments</div>
                    <div className="mt-1 font-mono text-[11px] opacity-70">{formatINR(instalmentAmount)} × 3</div>
                  </button>
                </div>
              </div>

              <div className="mt-6 space-y-2 text-sm font-mono">
                <div className="flex justify-between text-novya-muted">
                  <span>Base tuition</span>
                  <span>{formatINR(base)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-novya-accent" data-testid="checkout-discount-row">
                    <span>Referral discount</span>
                    <span>− {formatINR(discount)}</span>
                  </div>
                )}
                {creditApplied > 0 && (
                  <div className="flex justify-between text-novya-accent" data-testid="checkout-credit-row">
                    <span>Credit applied</span>
                    <span>− {formatINR(creditApplied)}</span>
                  </div>
                )}
                {isInstalments && (
                  <div className="flex justify-between text-novya-muted" data-testid="checkout-plan-note">
                    <span>Split into</span>
                    <span>3 monthly instalments</span>
                  </div>
                )}
                <div className="flex justify-between text-novya-ink font-bold pt-2 border-t border-novya-border">
                  <span>{isInstalments ? "Due today (Instalment 1/3)" : "You pay"}</span>
                  <span data-testid="checkout-final-amount">{formatINR(finalAmount)}</span>
                </div>
                {isInstalments && (
                  <div className="text-[11px] text-novya-muted pt-1">
                    Next {formatINR(instalmentAmount)} due in 30 days · final {formatINR(totalAfterDiscount - instalmentAmount * 2)} in 60 days. Pay each from your dashboard.
                  </div>
                )}
              </div>

              <button
                data-testid="enroll-btn"
                onClick={handleEnroll}
                disabled={enrolling}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-novya-ink text-white font-medium hover:bg-novya-accent transition-colors disabled:opacity-50"
              >
                {enrolling ? "Redirecting to PayU…" : `Enroll — ${formatINR(finalAmount)}`}
                {!enrolling && <ArrowRight size={18} />}
              </button>
              <p className="mt-3 text-[11px] font-mono tracking-wide text-novya-muted text-center">
                Secure payment via PayU · Cards, UPI, netbanking, wallets
              </p>
            </div>
          </div>

          <div className="md:col-span-5 aspect-[4/5] overflow-hidden bg-novya-dark">
            <img src={program.hero_image} alt={program.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="border-b border-novya-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-3 font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted">
            Outcomes
          </div>
          <div className="md:col-span-9">
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter leading-tight text-novya-ink">
              What you&apos;ll walk out with.
            </h2>
            <ul className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {program.outcomes.map((o) => (
                <li key={o} className="border-l border-novya-ink pl-4 flex gap-3">
                  <Check size={18} className="text-novya-accent mt-1 shrink-0" />
                  <span className="text-novya-ink leading-snug">{o}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="border-b border-novya-border bg-novya-dark text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-3 font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
              Curriculum
            </div>
            <div className="md:col-span-9">
              <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter leading-tight">
                Module by module.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {program.modules.map((m, i) => (
              <div key={m.title} data-testid={`module-${i}`} className="bg-novya-dark p-8 md:p-10">
                <div className="flex justify-between items-start">
                  <div className="font-mono text-xs tracking-[0.3em] text-novya-accent">
                    0{i + 1}
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">
                    {m.weeks} weeks
                  </div>
                </div>
                <h3 className="mt-6 font-display font-bold text-2xl tracking-tight">{m.title}</h3>
                <ul className="mt-4 space-y-2 text-sm text-white/60">
                  {m.topics.map((t) => <li key={t}>· {t}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20 border-b border-novya-border">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="max-w-2xl">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted">
                Curriculum PDF
              </div>
              <h2 className="mt-4 font-display font-black text-3xl md:text-4xl tracking-tighter leading-tight text-novya-ink">
                Want the full syllabus?
              </h2>
              <p className="mt-3 text-novya-muted max-w-lg leading-relaxed">
                Drop your email and we&apos;ll send the module-by-module breakdown as a PDF you can share with your team or manager.
              </p>
            </div>
            <button
              onClick={() => setShowSyllabus(true)}
              data-testid="download-syllabus-btn"
              className="inline-flex items-center gap-2 px-6 py-3 border border-novya-ink text-novya-ink font-medium hover:bg-novya-ink hover:text-white transition-colors"
            >
              <Download size={16} /> Download syllabus
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {program.testimonials && program.testimonials.length > 0 && (
        <section className="border-b border-novya-border bg-novya-bg" data-testid="testimonials-section">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
              <div className="md:col-span-3 font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted">
                Alumni
              </div>
              <div className="md:col-span-9">
                <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter leading-tight text-novya-ink">
                  They made the leap.
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {program.testimonials.slice(0, 3).map((t, i) => (
                <div key={i} data-testid={`testimonial-${i}`} className="bg-white border border-novya-border p-8 flex flex-col">
                  <Quote size={22} className="text-novya-accent" />
                  <p className="mt-5 text-novya-ink leading-relaxed text-[15px] flex-1">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="mt-8 flex items-center gap-3 pt-6 border-t border-novya-border">
                    <div className="w-10 h-10 rounded-full bg-novya-ink flex items-center justify-center text-white font-display font-bold text-sm">
                      {t.initials}
                    </div>
                    <div>
                      <div className="font-medium text-novya-ink text-sm">{t.name}</div>
                      <div className="font-mono text-[10px] tracking-wide text-novya-muted">{t.role} · {t.cohort_year}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <Link to="/programs" className="font-mono text-xs tracking-[0.2em] uppercase text-novya-muted link-underline">← All programs</Link>
        </div>
      </section>

      {showSyllabus && (
        <SyllabusModal
          programSlug={slug}
          programTitle={program.title}
          onClose={() => setShowSyllabus(false)}
        />
      )}
    </div>
  );
}
